import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import classes from './styles.module.scss';
import { actionNotifier } from '../../components/ui/toast';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '680px',
    maxWidth: 'calc(100vw - 32px)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: '90vh',
    overflowY: 'auto',
    outline: 'none',
};

const defaultFaqState = {
    id: null,
    section: '',
    category: '',
    question: '',
    answer: '',
    sortOrder: '1',
};

const FaqFormModal = ({ open, handleClose, handleSave, initialData, isLoading }) => {
    const [faqData, setFaqData] = useState(defaultFaqState);

    useEffect(() => {
        if (open && initialData) {
            setFaqData({
                id: initialData.id || null,
                section: initialData.section || '',
                category: initialData.category || '',
                question: initialData.question || '',
                answer: initialData.answer || '',
                sortOrder: String(initialData.sortOrder || 1),
            });
            return;
        }

        if (open) {
            setFaqData(defaultFaqState);
        }
    }, [initialData, open]);

    const handleChange = (field, value) => {
        setFaqData((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const onSubmit = async (event) => {
        event.preventDefault();

        if (!faqData.section.trim() || !faqData.category.trim() || !faqData.question.trim() || !faqData.answer.trim()) {
            actionNotifier.error('Please fill all required fields.');
            return;
        }

        if (!faqData.sortOrder || Number(faqData.sortOrder) < 1) {
            actionNotifier.error('Sort order must be 1 or greater.');
            return;
        }

        try {
            await handleSave(faqData);
            handleClose();
        } catch (error) {
            actionNotifier.error(error?.data?.message || 'Unable to save FAQ.');
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <div className={classes.modal_header}>
                    <h2 className={classes.modal_title}>{initialData ? 'Edit FAQ' : 'Add FAQ'}</h2>
                    <button onClick={handleClose} className={classes.close_modal_btn} type="button">
                        <CloseIcon />
                    </button>
                </div>

                <form onSubmit={onSubmit} className={classes.modal_form}>
                    <div className={classes.form_grid}>
                        <div className={classes.form_group}>
                            <label>Section *</label>
                            <input
                                type="text"
                                placeholder="Enter section"
                                value={faqData.section}
                                onChange={(event) => handleChange('section', event.target.value)}
                            />
                        </div>

                        <div className={classes.form_group}>
                            <label>Category *</label>
                            <input
                                type="text"
                                placeholder="Enter category"
                                value={faqData.category}
                                onChange={(event) => handleChange('category', event.target.value)}
                            />
                        </div>

                        <div className={`${classes.form_group} ${classes.form_group_full}`}>
                            <label>Question *</label>
                            <input
                                type="text"
                                placeholder="Enter question"
                                value={faqData.question}
                                onChange={(event) => handleChange('question', event.target.value)}
                            />
                        </div>

                        <div className={`${classes.form_group} ${classes.form_group_full}`}>
                            <label>Answer *</label>
                            <textarea
                                placeholder="Enter answer"
                                value={faqData.answer}
                                onChange={(event) => handleChange('answer', event.target.value)}
                            />
                        </div>

                        <div className={classes.form_group}>
                            <label>Sort Order</label>
                            <input
                                type="number"
                                min="1"
                                value={faqData.sortOrder}
                                onChange={(event) => handleChange('sortOrder', event.target.value)}
                            />
                        </div>
                    </div>

                    <div className={classes.button_container}>
                        <button type="button" onClick={handleClose} className={classes.cancel_btn}>
                            Cancel
                        </button>
                        <button type="submit" className={classes.submit_btn} disabled={isLoading}>
                            {isLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Box>
        </Modal>
    );
};

export default FaqFormModal;
